import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, type SearchSchemaInput, useNavigate } from '@tanstack/react-router'
import {
  activeMeetingsQueryOptions,
  MeetingRefs,
  MeetingRoster,
  MeetingStateBadge,
  meetingsInfiniteQueryOptions,
  rosterCountLabel,
} from '@/entities/meeting'
import { projectsQueryOptions } from '@/entities/project'
import { useRequireCustomer } from '@/entities/session'
import {
  cn,
  formatDurationBetween,
  formatElapsedSince,
  formatMeetingStart,
  formatTime,
  zoneHint,
} from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Card } from '@/shared/ui/card'
import { EmptyState } from '@/shared/ui/empty-state'
import { PageHeader } from '@/shared/ui/page-header'
import { Select } from '@/shared/ui/select'
import { Spinner } from '@/shared/ui/spinner'
import { ZoneNote } from '@/shared/ui/zone-note'

// Витрина заказчика: только те созвоны, которые сотрудник открыл его компании
// (фильтрует бэк по customer-сессии). Ни создания, ни приглашений, ни тегов —
// клиент здесь читатель, а не участник рабочего процесса команды.
const TABS = [
  { value: 'scheduled', label: 'Предстоящие', empty: 'Предстоящих созвонов нет' },
  { value: 'ended', label: 'Прошедшие', empty: 'Прошедших созвонов нет' },
] as const

type Tab = (typeof TABS)[number]['value']

interface MyMeetingsSearch {
  tab?: 'ended'
  project?: string
}

export const Route = createFileRoute('/my/meetings')({
  // Дефолтная вкладка (предстоящие) живёт вне URL — ?tab= появляется только у
  // не-дефолтной, ссылки без search валидны. Срез по проекту живёт в URL: такой
  // ссылкой делятся и её открывают повторно.
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput): MyMeetingsSearch => ({
    ...(search.tab === 'ended' ? { tab: 'ended' as const } : {}),
    ...(typeof search.project === 'string' && search.project !== ''
      ? { project: search.project }
      : {}),
  }),
  head: () => ({ meta: [{ title: 'Мои созвоны · STIT Телемост' }] }),
  component: MyMeetingsPage,
})

function MyMeetingsPage() {
  useRequireCustomer()
  const navigate = useNavigate()
  const search = Route.useSearch()
  const tab: Tab = search.tab ?? 'scheduled'
  const project = search.project

  // Справочник проектов своей компании: бэкенд скоупит его по company_id из
  // claim. Пока ручка заказчику закрыта, справочник пуст — витрина работает
  // без фильтра, а не падает.
  const projectDirectory = useQuery({ ...projectsQueryOptions(), retry: false })
  const projects = projectDirectory.data ?? []

  // Идущие сейчас — отдельный запрос: в них можно войти прямо отсюда. Срез тот
  // же, что у списка: иначе секция противоречила бы выбранному фильтру.
  const ongoingQuery = useQuery(activeMeetingsQueryOptions({ project }))
  const listQuery = useInfiniteQuery(meetingsInfiniteQueryOptions(tab, { project }))

  const ongoing = ongoingQuery.data ?? []
  const rest = listQuery.data?.pages.flatMap((p) => p.items) ?? []
  const isLoading = ongoingQuery.isLoading || listQuery.isLoading
  const isError = ongoingQuery.isError || listQuery.isError
  const current = TABS.find((t) => t.value === tab) ?? TABS[0]

  return (
    <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
      <PageHeader
        title="Мои созвоны"
        description="Созвоны, которые команда открыла вашей компании: запись и расшифровка — внутри."
      />

      {/* Срез отображения: сужает список, а не круг доступного — все открытые
          вашей компании созвоны остаются на «Все проекты». */}
      {projects.length > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <Select
            aria-label="Фильтр по проекту"
            className="w-full sm:w-64"
            value={project ?? ''}
            placeholder="Все проекты"
            options={projects.map((p) => ({ value: p.id, label: p.name }))}
            onChange={(id) =>
              void navigate({
                to: '/my/meetings',
                search: (prev: MyMeetingsSearch) => ({ ...prev, project: id || undefined }),
              })
            }
          />
        </div>
      )}

      {isLoading && <Spinner label="Загрузка созвонов" />}
      {isError && (
        <p className="text-accent">Не удалось загрузить созвоны. Обновите страницу и повторите.</p>
      )}

      {!isLoading && !isError && ongoing.length > 0 && (
        <section className="mt-8 flex flex-col gap-3">
          {/* Зона подписана один раз на секцию: тег в каждой строке — частокол,
              а вопрос «в какой зоне» у колонки времени один. */}
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <h2 className="t-h3">Идут сейчас</h2>
            <ZoneNote label="время" />
          </div>
          <ul className="grid grid-cols-1 gap-3">
            {ongoing.map((m) => {
              const startedAt = m.startedAt ?? m.createdAt
              // Длительность — по часам клиента: отставшие часы дают «меньше
              // минуты», а не пустоту вместо цифры.
              const runningFor = formatElapsedSince(startedAt)
              const startedTime = formatTime(startedAt)
              return (
                <li key={m.id}>
                  <Card>
                    <div className="flex items-start justify-between gap-3">
                      <Link to="/my/meetings/$id" params={{ id: m.id }} className="min-w-0 flex-1">
                        <p className="t-h3 mb-[1lh] truncate text-text-primary">{m.title}</p>
                        <p title={zoneHint()} className="text-sm text-text-secondary">
                          {rosterCountLabel(m.roster)} · идёт
                          {runningFor ? `\u00A0${runningFor}` : ''}
                          {startedTime ? ` · с\u00A0${startedTime}` : ''}
                        </p>
                        {/* Кто в звонке: заказчик видит состав так же, как команда. */}
                        <MeetingRoster roster={m.roster} />
                      </Link>
                      <Button
                        size="sm"
                        onClick={() => void navigate({ to: '/join/$id', params: { id: m.id } })}
                      >
                        Войти
                      </Button>
                    </div>
                    {/* Только проект: компания у заказчика всегда своя. */}
                    <MeetingRefs meeting={m} hideCompany className="mt-3" />
                  </Card>
                </li>
              )
            })}
          </ul>
        </section>
      )}

      {!isLoading && !isError && (
        <section className="mt-15 flex flex-col gap-5">
          {/* Зона ленты — рядом с вкладками: заголовок колонки времени для всех
              карточек ниже. Заказчик смотрит витрину со своей стороны, и зона
              обязана называться у него так же явно, как у команды. */}
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
            <nav aria-label="Фильтр созвонов" className="flex gap-5">
              {TABS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  aria-pressed={t.value === tab}
                  onClick={() =>
                    void navigate({
                      to: '/my/meetings',
                      // Смена вкладки не сбрасывает выбранный проект.
                      search: (prev: MyMeetingsSearch) => ({
                        ...prev,
                        ...(t.value === 'ended' ? { tab: 'ended' as const } : { tab: undefined }),
                      }),
                    })
                  }
                  className={cn(
                    'relative pb-2 font-mono text-xs font-medium uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent',
                    t.value === tab
                      ? 'text-text-primary after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-accent'
                      : 'text-text-tertiary hover:text-text-primary',
                  )}
                >
                  {t.label}
                </button>
              ))}
            </nav>
            <ZoneNote label="время" />
          </div>

          {rest.length === 0 && ongoing.length === 0 && (
            <EmptyState
              title={current.empty}
              description={
                project
                  ? 'В этом проекте пока пусто — снимите фильтр, чтобы увидеть остальные созвоны.'
                  : 'Созвон появится здесь, когда команда откроет его вашей компании.'
              }
            />
          )}
          {rest.length === 0 && ongoing.length > 0 && <p className="label mt-2">{current.empty}</p>}

          {rest.length > 0 && (
            <>
              <ul className="grid grid-cols-1 gap-3">
                {rest.map((m) => {
                  let timeText: string | undefined
                  if (m.state === 'ended' && m.endedAt) {
                    const startedAt = m.startedAt ?? m.createdAt
                    const duration = formatDurationBetween(startedAt, m.endedAt)
                    timeText = `${formatMeetingStart(startedAt)} — ${formatTime(m.endedAt)}${duration ? ` · ${duration}` : ''}`
                  } else {
                    const planned = formatMeetingStart(m.scheduledAt ?? m.createdAt)
                    if (planned) timeText = `запланирован на ${planned}`
                  }
                  return (
                    <li key={m.id}>
                      <Card interactive>
                        <div className="flex items-start justify-between gap-3">
                          <Link
                            to="/my/meetings/$id"
                            params={{ id: m.id }}
                            className="min-w-0 flex-1"
                          >
                            <p className="t-h3 mb-[1lh] truncate text-text-primary">{m.title}</p>
                            {timeText && (
                              <p title={zoneHint()} className="text-sm text-text-secondary">
                                {timeText}
                              </p>
                            )}
                            <MeetingRoster roster={m.roster} />
                          </Link>
                          {/* mt-[5px]: верх бейджа на уровне верха букв заголовка. */}
                          <MeetingStateBadge state={m.state} className="mt-[5px]" />
                        </div>
                        <MeetingRefs meeting={m} hideCompany className="mt-3" />
                      </Card>
                    </li>
                  )
                })}
              </ul>
              {listQuery.hasNextPage && (
                <div className="mt-2 flex justify-center">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={listQuery.isFetchingNextPage}
                    onClick={() => void listQuery.fetchNextPage()}
                  >
                    {listQuery.isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
                  </Button>
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  )
}
